import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaFileExcel, FaSyncAlt, FaTimes, FaUpload, FaCheckCircle, FaExclamationCircle, FaSpinner, FaLock, FaChevronDown, FaChevronUp, FaClock, FaTrash, FaCheck, FaSave, FaFile, FaEye, FaBookOpen, FaChartLine, FaDownload } from 'react-icons/fa';
import StepCard from './StepCard';
import FilePreviewModal from '../Tax Filling/FilePreviewModal';
import FeedbackModal from '../Tax Filling/FeedbackModal';
import { useTheme } from '../../context/SocketContext';
import { useRealtimeRefresh } from '../../context/RealtimeSyncContext';
import PageHeader from '../../components/PageHeader';
import FinancialStatementViewer from '../../components/FinancialStatementViewer';

import { FullPageSkeleton } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';
import {
  usePortalContent,
  getBookkeepingPageMeta,
  mergeBookkeepingSteps,
  buildCategoryFileState,
} from '../../portal';

const SPECIAL_BOOKKEEPING_USER_IDS = ['691c4908d1fab55e7f2c2ef5', '690c8991d6e0a0f153191140'];

const UserBookkeeping = () => {
  const { currentThemeColors } = useTheme();
  const navigate = useNavigate();
  const { content: bookkeepingContent, loading: portalLoading } = usePortalContent('bookkeeping');
  const bookkeepingMeta = useMemo(() => getBookkeepingPageMeta(bookkeepingContent), [bookkeepingContent]);
  const stepsConfig = bookkeepingMeta.stepCards;
  const step1Copy = bookkeepingMeta.step1Modal;
  const queriesCopy = bookkeepingMeta.queriesModal;
  const step3Copy = bookkeepingMeta.step3Modal;
  const uploadCopy = bookkeepingMeta.uploadModal;
  const toastCopy = bookkeepingMeta.toasts;
  const checklistCategories = bookkeepingMeta.checklistCategories;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookkeepingData, setBookkeepingData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFilesByCategory, setSelectedFilesByCategory] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [activeStepIndex, setActiveStepIndex] = useState(null);
  const [showQueriesModal, setShowQueriesModal] = useState(false);
  const [queries, setQueries] = useState([]);
  const [submittingQueries, setSubmittingQueries] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const autoSaveTimerRef = React.useRef(null);

  // Bookkeeping Step 3 states
  const [showStep3Modal, setShowStep3Modal] = useState(false);
  const [step3Action, setStep3Action] = useState(""); // "yes" or "no"
  const [step3Comment, setStep3Comment] = useState("");
  const [step3Submitting, setStep3Submitting] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Published Financial Reports states
  const [userReports, setUserReports] = useState([]);
  const [selectedReportPreview, setSelectedReportPreview] = useState(null);

  const fetchUserReports = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/financial-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.reports) {
        setUserReports(data.reports);
      }
    } catch (err) {
      console.error("fetchUserReports error:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const handleSubmitStep3Response = async (actionType) => {
    if (actionType === 'reject' && !step3Comment.trim()) {
      toast.warning(step3Copy.commentRequiredToast || 'Please provide a review comment explaining what needs to be adjusted.');
      return;
    }

    const pendingEntry = [...(userData?.bookkeepingStep3History || [])]
      .reverse()
      .find(h => h.status === 'pending');

    setStep3Submitting(true);
    try {
      const token = localStorage.getItem('token');

      const submittedQuestions = pendingEntry && pendingEntry.questions
        ? pendingEntry.questions.map((q, qIdx) => ({
          questionText: q.questionText,
          options: q.options || [],
          selectedAnswer: ""
        }))
        : [];

      const bodyData = {
        action: actionType === 'approve' ? 'approve' : 'reject',
        comment: actionType === 'reject' ? step3Comment : ''
      };

      if (submittedQuestions.length > 0) {
        bodyData.questions = submittedQuestions;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${getEffectiveUserId()}/step3/respond`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(bodyData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit response');
      }

      const data = await response.json();
      setUserData(data.user);

      if (data.user.bookkeepingSteps) {
        const updatedSteps = mergeBookkeepingSteps(data.user.bookkeepingSteps, stepsConfig);
        setBookkeepingSteps(updatedSteps);
      }

      toast.success(actionType === 'approve'
        ? (step3Copy.approveSuccessToast || 'Draft financials approved successfully!')
        : (step3Copy.rejectSuccessToast || 'Review comment submitted successfully!'));
      setShowStep3Modal(false);
      setStep3Comment("");
      setStep3Action("");
      setSelectedAnswers({});
    } catch (err) {
      console.error("Error submitting step 3 response:", err);
      toast.error(err.message || "Failed to submit response.");
    } finally {
      setStep3Submitting(false);
    }
  };

  const handleOpenQueriesModal = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/queries/my-assigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch assigned queries');
      const data = await response.json();
      setQueries(data.queries || []);
      setShowQueriesModal(true);
      setAutoSaveStatus('idle');
    } catch (err) {
      toast.error("Failed to load queries: " + err.message);
    }
  };

  const handleSaveClientComments = async (isSubmit = false, isSilent = false, queriesToSave = queries) => {
    const step2Status = bookkeepingSteps.find(s => s.step === 2)?.status || 'pending';
    if (step2Status === 'submitted' || step2Status === 'completed') {
      if (!isSilent) {
        toast.warning(toastCopy.queriesLocked || 'These bookkeeping queries are locked because comments have already been submitted.');
      }
      return;
    }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    if (isSilent) {
      setAutoSaveStatus('saving');
    } else {
      setSubmittingQueries(true);
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/queries/save-assigned`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ queries: queriesToSave, status: isSubmit ? 'submitted' : 'client-pending' })
        }
      );

      if (!response.ok) throw new Error('Failed to save queries');
      const data = await response.json();

      // Update local state
      setQueries(data.queries || []);
      setUserData(data.user);
      if (data.user.bookkeepingSteps) {
        const updatedSteps = mergeBookkeepingSteps(data.user.bookkeepingSteps, stepsConfig);
        setBookkeepingSteps(updatedSteps);
      }

      if (isSilent) {
        setAutoSaveStatus('saved');
        setTimeout(() => {
          setAutoSaveStatus((current) => current === 'saved' ? 'idle' : current);
        }, 3000);
      } else {
        if (isSubmit) {
          toast.success(toastCopy.commentsSubmitted || 'Your comments have been submitted successfully!');
          setShowQueriesModal(false);
        } else {
          toast.success(toastCopy.commentsDraftSaved || 'Your comments draft has been saved successfully!');
        }
      }
    } catch (err) {
      console.error("Error saving client comments:", err);
      if (isSilent) {
        setAutoSaveStatus('error');
      } else {
        toast.error("Failed to save comments: " + err.message);
      }
    } finally {
      if (!isSilent) {
        setSubmittingQueries(false);
      }
    }
  };

  const handleClientCommentChange = (index, val) => {
    const step2Status = bookkeepingSteps.find(s => s.step === 2)?.status || 'pending';
    if (step2Status === 'submitted' || step2Status === 'completed') return;

    const updated = [...queries];
    updated[index].clientComment = val;
    setQueries(updated);

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('saving');
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveClientComments(false, true, updated);
    }, 1500);
  };

  const handleClientReplyChange = (index, val) => {
    const step2Status = bookkeepingSteps.find(s => s.step === 2)?.status || 'pending';
    if (step2Status === 'submitted' || step2Status === 'completed') return;

    const updated = [...queries];
    updated[index].clientReply = val;
    setQueries(updated);

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('saving');
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveClientComments(false, true, updated);
    }, 1500);
  };

  const [bookkeepingSteps, setBookkeepingSteps] = useState([]);

  const getEffectiveUserId = () => userData?.bookkeepingParentId || userData?._id;
  const isCollaboratorUser = () => !!(userData?.isBookkeepingCollaborator || userData?.isAssignee);

  useEffect(() => {
    fetchBookkeepingData();
  }, []);

  useEffect(() => {
    if (!checklistCategories.length) return;
    setSelectedFilesByCategory((prev) => {
      const next = buildCategoryFileState(checklistCategories);
      Object.keys(next).forEach((key) => {
        next[key] = prev[key] || [];
      });
      return next;
    });
  }, [checklistCategories]);

  const resetCategoryFiles = () => setSelectedFilesByCategory(buildCategoryFileState(checklistCategories));

  useEffect(() => {
    if (!userData || portalLoading) return;
    setBookkeepingSteps(mergeBookkeepingSteps(userData.bookkeepingSteps, stepsConfig));
  }, [stepsConfig, userData, portalLoading]);

  const fetchBookkeepingData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setIsRefreshing(true);
      setError('');

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const userDataResponse = await userResponse.json();
      setUserData(userDataResponse.user);

      if (userDataResponse.user.bookkeepingSteps && userDataResponse.user.bookkeepingSteps.length > 0) {
        setBookkeepingSteps(mergeBookkeepingSteps(userDataResponse.user.bookkeepingSteps, stepsConfig));
      } else {
        setBookkeepingSteps(mergeBookkeepingSteps([], stepsConfig));
      }

      const isCollab = !!(userDataResponse.user.isBookkeepingCollaborator || userDataResponse.user.isAssignee);

      // Show feedback modal if Step 4 is completed but feedback not submitted (only for primary users)
      const step4Completed = userDataResponse.user.bookkeepingSteps?.find(s => s.step === 4)?.status === 'completed';
      if (!isCollab && step4Completed && (!userDataResponse.user.bookkeepingFeedback || !userDataResponse.user.bookkeepingFeedback.rating)) {
        setShowFeedbackModal(true);
      }
      const parentId = userDataResponse.user.bookkeepingParentId || userDataResponse.user._id;

      if (!isCollab) {
        const bookkeepingResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${parentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!bookkeepingResponse.ok) {
          if (bookkeepingResponse.status === 404) {
            setBookkeepingData(null);
            return;
          }
          throw new Error('Failed to fetch bookkeeping data');
        }

        const data = await bookkeepingResponse.json();
        setBookkeepingData(data);
      } else {
        setBookkeepingData(null);
      }

      // Fetch user's published financial reports
      await fetchUserReports();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useRealtimeRefresh(['bookkeeping', 'profile'], fetchBookkeepingData);

  const handleFeedbackSubmit = async (rating, comment) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.warning("Please log in to submit feedback.");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: getEffectiveUserId(),
          rating,
          comment,
          serviceType: 'bookkeeping'
        })
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.message || `Failed to submit feedback: ${response.status}`);
      }

      setUserData(prev => ({
        ...prev,
        bookkeepingFeedback: {
          rating,
          comment,
          submittedAt: new Date()
        }
      }));

      setShowFeedbackModal(false);
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(`Failed to submit feedback: ${error.message || 'Please try again.'}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isUploadStep = (index) => [0, 2].includes(index);

  const handleStepClick = (stepIndex) => {
    if (isUploadStep(stepIndex) && showSpecialSteps) {
      setActiveStepIndex(stepIndex);
      setShowUploadModal(true);
      setUploadMessage('');
      setUploadError('');
    }
  };

  const handleCloseUploadModal = () => {
    if (uploading) return;
    setShowUploadModal(false);
    setActiveStepIndex(null);
    setSelectedFiles([]);
    setSelectedFilesByCategory(buildCategoryFileState(checklistCategories));
    setUploadMessage('');
    setUploadError('');
  };

  const handleCategoryFileSelection = (category, filesList) => {
    const filesArray = Array.from(filesList);
    const oversized = filesArray.some(file => file.size > 100 * 1024 * 1024);
    if (oversized) {
      setUploadError(toastCopy.maxFileSize || 'One or more files exceed the maximum limit of 100 MB.');
      return;
    }

    setSelectedFilesByCategory(prev => ({
      ...prev,
      [category]: [...prev[category], ...filesArray]
    }));
    setUploadError('');
    setUploadMessage('');
  };

  const handleRemoveSelectedFile = (category, index) => {
    setSelectedFilesByCategory(prev => {
      const updated = [...prev[category]];
      updated.splice(index, 1);
      return {
        ...prev,
        [category]: updated
      };
    });
  };

  const handleDeleteUploadedFile = async (fileId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setUploading(true);
      setUploadError('');
      setUploadMessage('');

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${getEffectiveUserId()}/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to delete file.');
      }

      setUploadMessage('File removed successfully.');
      toast.success('File removed successfully.');
      await fetchBookkeepingData();
    } catch (err) {
      setUploadError(err.message || 'Failed to delete file.');
      toast.error(err.message || 'Failed to delete file.');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveOrSubmitChecklist = async (isSubmit) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!userData?._id) {
      setUploadError('User information is missing. Please refresh and try again.');
      toast.error('User information is missing. Please refresh and try again.');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      setUploadMessage('');

      // 1. Upload selected files category by category
      for (const [category, files] of Object.entries(selectedFilesByCategory)) {
        if (files.length === 0) continue;

        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        formData.append('step', category);

        const uploadResp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${getEffectiveUserId()}/files?isDraft=${!isSubmit}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        if (!uploadResp.ok) {
          const errData = await uploadResp.json().catch(() => ({}));
          throw new Error(errData.message || `Failed to upload files for ${category}.`);
        }
      }

      // 2. Update step status on backend
      const newStatus = isSubmit ? 'submitted' : 'in-progress';
      const statusResp = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${getEffectiveUserId()}/step`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          stepNumber: 1,
          status: newStatus
        })
      });

      if (!statusResp.ok) {
        const errData = await statusResp.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to update step status.');
      }

      setUploadMessage(isSubmit
        ? (toastCopy.checklistSubmitted || 'Checklist submitted successfully!')
        : (toastCopy.checklistDraftSaved || 'Checklist progress saved successfully!'));
      toast.success(isSubmit
        ? (toastCopy.checklistSubmitted || 'Checklist submitted successfully!')
        : (toastCopy.checklistDraftSaved || 'Checklist progress saved successfully!'));
      resetCategoryFiles();
      await fetchBookkeepingData();

      // Close modal after a brief delay
      setTimeout(() => {
        setShowUploadModal(false);
      }, 1000);

    } catch (err) {
      setUploadError(err.message || 'Failed to save checklist.');
      toast.error(err.message || 'Failed to save checklist.');
    } finally {
      setUploading(false);
    }
  };



  if (loading || portalLoading) {
    return (
      <div className="space-y-6">
        <FullPageSkeleton message={bookkeepingMeta.loadingMessage} />
      </div>
    );
  }

  const isCollaborator = isCollaboratorUser();
  const showSpecialSteps = userData && !isCollaborator && SPECIAL_BOOKKEEPING_USER_IDS.includes(userData._id);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FaBookOpen}
        title={bookkeepingMeta.pageTitle}
        subtitle={bookkeepingMeta.pageSubtitle}
      />

      {/* Bundle Activation Message */}
      {userData?.isBundlePurchased && (
        <div className="p-4 rounded-xl bg-green-500 text-white flex items-center gap-3 shadow-lg">
          <FaCheckCircle className="w-6 h-6 flex-shrink-0" />
          <span className="font-semibold">{bookkeepingMeta.bundleBannerText}</span>
        </div>
      )}

      {/* Bookkeeping Service Due Date Alert */}
      {userData?.bookkeepingService?.dueDate && (
        <div className="p-6 rounded-2xl bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-purple-500/10 border border-indigo-500/20 shadow-xl backdrop-blur-md flex flex-col md:flex-row md:items-center justify-between gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '50ms', animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/30">
              <FaClock className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h4 className="font-bold text-dark-800 dark:text-white text-base">{bookkeepingMeta.scheduleBannerTitle}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {bookkeepingMeta.scheduleFrequencyPrefix} <span className="font-bold text-indigo-600 dark:text-indigo-400">{userData.bookkeepingService.frequency}</span>, {bookkeepingMeta.scheduleStartedPrefix} {new Date(userData.bookkeepingService.startDate).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end justify-center">
            <span className="text-xs uppercase font-bold tracking-wider text-gray-400">{bookkeepingMeta.scheduleDueDateLabel}</span>
            <span className="text-lg font-bold text-red-600 dark:text-red-400 mt-0.5">
              {userData.bookkeepingService.dueDateText || new Date(userData.bookkeepingService.dueDate).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>
      )}

      {/* Bookkeeping Steps */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 opacity-0 animate-fade-in-up"
        style={{ animationDelay: '100ms', animationFillMode: 'forwards', minHeight: '200px' }}
      >
        {bookkeepingSteps.map((s, index) => (
          <div
            key={`wrapper-${s.step}`}
            className="h-full"
          >
            <StepCard
              step={s.step}
              title={s.title}
              status={s.status}
              progress={0}
              totalQuestions={0}
              onClick={() => {
                if (isCollaborator && s.step !== 2) {
                  return;
                }
                if (s.step === 1) {
                  setActiveStepIndex(0);
                  setShowUploadModal(true);
                } else if (s.step === 2) {
                  if (s.status === 'in-progress') {
                    toast.info(toastCopy.queriesUnderReview || 'Bookkeeping queries are currently under review by the Bookscomply team. You will be able to access this section once queries are ready for your input.');
                    return;
                  }
                  if (s.status === 'pending') {
                    toast.warning(toastCopy.stepLocked || 'This step is currently locked. Please complete the previous steps first.');
                    return;
                  }
                  handleOpenQueriesModal();
                } else if (s.step === 3) {
                  setShowStep3Modal(true);
                } else if (s.step === 4) {
                  if (s.status === 'completed') {
                    if (!userData?.bookkeepingFeedback || !userData?.bookkeepingFeedback.rating) {
                      setShowFeedbackModal(true);
                    } else {
                      toast.info(toastCopy.finalBooksReady || 'Your final books of accounts are ready. Thank you for your feedback!');
                    }
                  } else {
                    toast.warning(toastCopy.step4Locked || 'This step will be unlocked once previous steps are completed.');
                  }
                }
              }}
              isLocked={false}
              onPayClick={() => { }}
              completedAt={null}
              customInstructions={s.instructions}
              customStatusMessage={s.statusMessage}
            />
          </div>
        ))}
      </div>

      {/* Financial Reports & Statements Section */}
      <div className="opacity-0 animate-fade-in-up space-y-4" style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FaChartLine className="text-primary-500" /> Financial Reports & Statements
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Access your official Profit & Loss statements, Balance Sheets, and financial reports.
            </p>
          </div>
        </div>

        {userReports.length === 0 ? (
          <div className="p-8 bg-white dark:bg-dark-800 rounded-2xl border border-dashed border-gray-200 dark:border-dark-700 text-center shadow-xs">
            <FaFile className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">No published financial reports available yet.</p>
            <p className="text-[11px] text-gray-400 mt-1">Your team will publish your P&L and Balance Sheet reports here once processed.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {userReports.map((rep) => (
              <div key={rep._id} className="p-5 bg-white dark:bg-dark-800 rounded-2xl border border-gray-150 dark:border-dark-700 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider ${
                      rep.reportType === 'profit_and_loss'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                        : rep.reportType === 'balance_sheet'
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                        : 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
                    }`}>
                      {rep.reportType.replace(/_/g, ' ')}
                    </span>
                    <span className="text-[10px] text-gray-400 font-medium">
                      {new Date(rep.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>

                  <h4 className="text-base font-bold text-gray-900 dark:text-white mb-1 group-hover:text-primary-600 transition-colors">
                    {rep.title}
                  </h4>
                  {rep.period && (
                    <p className="text-xs text-gray-500 font-medium mb-3">
                      Period: <span className="font-semibold text-gray-700 dark:text-gray-300">{rep.period}</span>
                    </p>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-100 dark:border-dark-700/60 flex items-center justify-between">
                  {rep.fileUrl ? (
                    <a
                      href={`${process.env.REACT_APP_API_BASE_URL}${rep.fileUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2 px-3 bg-primary-50 hover:bg-primary-100 dark:bg-primary-950/30 dark:hover:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs font-bold rounded-xl transition-all border border-primary-200 dark:border-primary-800 flex items-center justify-center gap-2 shadow-xs"
                    >
                      <FaDownload className="w-3.5 h-3.5" />
                      <span>Download / View PDF</span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSelectedReportPreview(rep)}
                      className="w-full py-2 px-3 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/30 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-bold rounded-xl transition-all border border-indigo-200 dark:border-indigo-800 flex items-center justify-center gap-2 shadow-xs"
                    >
                      <FaEye className="w-3.5 h-3.5" />
                      <span>View Online Statement</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Online Statement Preview Modal */}
      {selectedReportPreview && (
        <div className="fixed inset-0 z-[999999] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 max-w-4xl w-full shadow-2xl border border-gray-100 dark:border-dark-700 animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedReportPreview.title}
                </h3>
                {selectedReportPreview.period && (
                  <p className="text-xs text-gray-500">Period: {selectedReportPreview.period}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedReportPreview(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
              >
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
              <FinancialStatementViewer report={selectedReportPreview} />
            </div>

            <div className="pt-4 border-t border-gray-100 dark:border-dark-700 flex justify-end">
              <button
                onClick={() => setSelectedReportPreview(null)}
                className="px-5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-dark-700 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-xl transition-all cursor-pointer"
              >
                Close Statement
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Error Alert */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <FaExclamationCircle className="w-5 h-5" />
            <span><strong>Error:</strong> {error}</span>
          </div>
          <button
            onClick={fetchBookkeepingData}
            disabled={isRefreshing}
            className="px-4 py-2 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
          >
            {isRefreshing ? bookkeepingMeta.errorRefreshingLabel : bookkeepingMeta.errorRetryLabel}
          </button>
        </div>
      )}

      {!isCollaborator && bookkeepingData ? (
        <div className="space-y-6">
          {/* Financial Records Table */}
          <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            <div
              className="p-4 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              style={{ background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))` }}
            >
              <div className="flex items-center gap-2">
                <FaFileExcel className="w-5 h-5" />
                <h2 className="font-semibold">{bookkeepingData.additionalTitle || bookkeepingMeta.financialRecordsTitle}</h2>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={fetchBookkeepingData}
                  disabled={isRefreshing}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  <FaSyncAlt className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    {bookkeepingData.headers.map((header, index) => (
                      <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {bookkeepingData.rows.filter(row => !row.include).map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-dark-800' : 'bg-gray-50 dark:bg-dark-700/50'}>
                      {bookkeepingData.headers.map((header, colIndex) => (
                        <td key={colIndex} className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">
                          {typeof row[header] === 'number' ? `$${row[header].toLocaleString()}` : row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!bookkeepingData.rows.filter(row => !row.include).length && (
                    <tr>
                      <td colSpan={bookkeepingData.headers.length} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        {bookkeepingMeta.financialRecordsEmpty}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>


        </div>
      ) : null}

      {/* Checklist Upload Modal (Step 1) */}
      {showUploadModal && activeStepIndex === 0 && (() => {
        const step1Status = bookkeepingSteps.find(s => s.step === 1)?.status || 'pending';
        const isStep1Locked = step1Status === 'submitted' || step1Status === 'completed';

        return createPortal(
          <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={handleCloseUploadModal}>
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center bg-gradient-to-r from-primary-500/5 to-accent-500/5">
                <div>
                  <h3 className="section-title flex items-center gap-2">
                    {step1Copy.title || 'Information Requirement Checklist'}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {step1Copy.subtitle || 'Upload all required documents below. You can save your draft and submit once completed.'}
                  </p>
                </div>
                <button onClick={handleCloseUploadModal} className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-dark-700 dark:hover:bg-dark-600 transition-colors">
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Lock Banner */}
              {isStep1Locked && (
                <div className="mx-6 mt-4 p-4 rounded-xl bg-blue-50/80 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 text-sm font-semibold border border-blue-200 dark:border-blue-900/30 flex items-center gap-2">
                  <FaLock className="w-4 h-4 text-blue-500 shrink-0" />
                  <span>{step1Copy.lockBanner || 'This checklist is locked for review. Contact admin if you need to reopen it.'}</span>
                </div>
              )}

              {/* Error / Success Banners */}
              {uploadError && (
                <div className="mx-6 mt-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 text-sm font-medium border border-red-200 dark:border-red-900/30">
                  {uploadError}
                </div>
              )}
              {uploadMessage && (
                <div className="mx-6 mt-4 p-3 rounded-xl bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400 text-sm font-medium border border-green-200 dark:border-green-900/30">
                  {uploadMessage}
                </div>
              )}

              {/* Checklist items */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {checklistCategories.map((cat) => {
                  const uploadedFiles = userData?.documents?.filter(doc => doc.step === cat.key) || [];
                  const newFiles = selectedFilesByCategory[cat.key] || [];
                  const totalFilesCount = uploadedFiles.length + newFiles.length;

                  return (
                    <div key={cat.key} className="p-5 rounded-2xl border border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/20 space-y-4 hover:border-primary-500/20 transition-all duration-200">
                      {/* Category Header */}
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 border-b border-gray-100 dark:border-dark-700/50 pb-3">
                        <div>
                          <h4 className="text-base font-bold text-dark-800 dark:text-white">
                            {cat.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {cat.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          {totalFilesCount > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-400 text-xs font-bold border border-green-200 dark:border-green-800/30 whitespace-nowrap">
                              <FaCheck className="w-2.5 h-2.5" /> {step1Copy.providedBadge || 'Provided'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 text-xs font-bold border border-amber-200 dark:border-amber-800/30 whitespace-nowrap">
                              <FaExclamationCircle className="w-2.5 h-2.5" /> {step1Copy.requiredBadge || 'Required'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Files list */}
                      {totalFilesCount > 0 && (
                        <div className="space-y-2">
                          {/* Server Uploaded files */}
                          {uploadedFiles.map((file) => (
                            <div key={file._id} className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-150">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <FaFile className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium text-dark-800 dark:text-white truncate" title={file.originalName}>
                                    {file.originalName}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {formatFileSize(file.size)} • Uploaded on {new Date(file.date).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-1.5 flex-shrink-0">
                                <button
                                  onClick={() => setPreviewFile({
                                    url: file.fileUrl,
                                    name: file.originalName,
                                    size: file.size,
                                    type: file.originalName?.split('.').pop()?.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg'
                                  })}
                                  className="p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:hover:bg-primary-950/30 rounded-lg transition-colors"
                                  title="Preview document"
                                >
                                  <FaEye className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteUploadedFile(file._id)}
                                  disabled={uploading || isStep1Locked}
                                  className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={isStep1Locked ? "Locked - Cannot delete" : "Delete document"}
                                >
                                  <FaTrash className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Local Selected files (Drafts) */}
                          {newFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-primary-50/20 dark:bg-primary-950/10 border border-dashed border-primary-200 dark:border-primary-800/40 shadow-sm">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <FaFile className="w-4 h-4 text-primary-500 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold text-primary-700 dark:text-primary-400 truncate" title={file.name}>
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-primary-500/70 dark:text-primary-400/60 mt-0.5">
                                    {formatFileSize(file.size)} • {step1Copy.readyToUploadLabel || 'Ready to upload'}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveSelectedFile(cat.key, idx)}
                                disabled={uploading}
                                className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors flex-shrink-0"
                                title="Remove selected file"
                              >
                                <FaTimes className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Document button */}
                      {!isStep1Locked && (
                        <div className="pt-1">
                          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-primary-300 dark:border-primary-700 hover:border-primary-500 bg-white dark:bg-dark-800 hover:bg-primary-50/50 dark:hover:bg-primary-950/10 cursor-pointer text-xs font-semibold text-primary-600 dark:text-primary-400 transition-all duration-150">
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) => handleCategoryFileSelection(cat.key, e.target.files)}
                              disabled={uploading}
                            />
                            <FaUpload className="w-3 h-3" />
                            <span>{step1Copy.addDocumentLabel || '+ Add Document'}</span>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50/50 dark:bg-dark-900/10">
                <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {step1Copy.supportedFormats || 'Supported formats: PDF, JPEG, PNG, Excel • Max 100 MB'}
                </div>
                <div className="flex gap-3 w-full sm:w-auto justify-end">
                  <button
                    onClick={handleCloseUploadModal}
                    disabled={uploading}
                    className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors disabled:opacity-50 text-sm font-semibold"
                  >
                    {step1Copy.closeLabel || 'Close'}
                  </button>
                  {!isStep1Locked && (
                    <>
                      <button
                        onClick={() => handleSaveOrSubmitChecklist(false)}
                        disabled={uploading}
                        className="px-4 py-2.5 rounded-xl bg-primary-50 dark:bg-primary-950/20 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-950/40 border border-primary-200 dark:border-primary-800/30 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-semibold"
                      >
                        {uploading ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaSave className="w-3.5 h-3.5" />}
                        <span>{step1Copy.saveDraftLabel || 'Save Draft'}</span>
                      </button>
                      <button
                        onClick={() => handleSaveOrSubmitChecklist(true)}
                        disabled={uploading}
                        className="px-5 py-2.5 rounded-xl bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/25 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-bold"
                      >
                        {uploading ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaCheck className="w-3.5 h-3.5" />}
                        <span>{step1Copy.submitLabel || 'Submit Checklist'}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Standard Upload Modal (Step 3: Draft Financials) */}
      {showUploadModal && activeStepIndex === 2 && createPortal(
        <div className="modal-overlay" onClick={handleCloseUploadModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
              <h3 className="text-lg font-semibold text-dark-800 dark:text-white">
                {uploadCopy.step3TitlePrefix || 'Upload Documents'} ({stepsConfig[activeStepIndex]?.title || 'Draft financials'})
              </h3>
              <button onClick={handleCloseUploadModal} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                <FaTimes className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {uploadCopy.step3Description || 'Upload your completed Financial Report documents (Step 3).'}
              </p>
              <div className="border-2 border-dashed border-gray-200 dark:border-dark-600 rounded-xl p-6 text-center">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <FaUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">{uploadCopy.dropHint || 'Click to select files or drag and drop'}</p>
                </label>
              </div>
              {selectedFiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-dark-800 dark:text-white mb-2">{uploadCopy.selectedFilesLabel || 'Selected Files'}</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedFiles.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-dark-700">
                        <span className="text-sm text-dark-800 dark:text-gray-300 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {uploadError && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                  {uploadError}
                </div>
              )}
              {uploadMessage && (
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm">
                  {uploadMessage}
                </div>
              )}
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-100 dark:border-dark-700">
              <button
                onClick={handleCloseUploadModal}
                disabled={uploading}
                className="flex-1 px-4 py-2 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 disabled:opacity-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  if (!selectedFiles.length) {
                    setUploadError('Please select at least one file.');
                    return;
                  }
                  const token = localStorage.getItem('token');
                  if (!token) return;
                  try {
                    setUploading(true);
                    setUploadError('');
                    setUploadMessage('');
                    const formData = new FormData();
                    selectedFiles.forEach(file => formData.append('files', file));
                    formData.append('step', stepsConfig[activeStepIndex]?.title || 'Draft financials');
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${getEffectiveUserId()}/files`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                      body: formData
                    });
                    if (!response.ok) {
                      const errData = await response.json().catch(() => ({}));
                      throw new Error(errData.message || 'Failed to upload files.');
                    }
                    const data = await response.json();
                    setUploadMessage(data.message || 'Files uploaded successfully.');
                    toast.success(data.message || 'Files uploaded successfully.');
                    setSelectedFiles([]);
                    await fetchBookkeepingData();
                  } catch (err) {
                    setUploadError(err.message || 'Failed to upload files.');
                    toast.error(err.message || 'Failed to upload files.');
                  } finally {
                    setUploading(false);
                  }
                }}
                disabled={uploading || !selectedFiles.length}
                className="flex-1 px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {uploading ? (uploadCopy.uploadingLabel || 'Uploading...') : (uploadCopy.uploadLabel || 'Upload Files')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {/* Bookkeeping Queries Modal for User */}
      {showQueriesModal && (() => {
        const step2Status = bookkeepingSteps.find(s => s.step === 2)?.status || 'pending';
        const isStep2Locked = step2Status === 'submitted' || step2Status === 'completed';

        return createPortal(
          <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-0 animate-fade-in" onClick={() => setShowQueriesModal(false)}>
            <div className="bg-white dark:bg-dark-800 w-full h-full flex flex-col overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
                <div>
                  <h3 className="text-lg font-semibold text-dark-800 dark:text-white flex items-center gap-2">
                    <FaClock className="text-primary-500" /> {queriesCopy.title || 'Pending Queries Table'}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {queriesCopy.subtitle || 'Please review the queries entered by Bookscomply and provide your comments in the last column.'}
                  </p>
                </div>
                <button onClick={() => setShowQueriesModal(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Lock Banner */}
              {isStep2Locked && (
                <div className="mx-6 mt-4 p-4 rounded-xl bg-blue-50/80 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 text-sm font-semibold border border-blue-200 dark:border-blue-900/30 flex items-center gap-2">
                  <FaLock className="w-4 h-4 text-blue-500 shrink-0" />
                  <span>{queriesCopy.lockBanner || 'These queries are locked because your comments have been submitted. Contact admin if you need to make changes.'}</span>
                </div>
              )}

              <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-dark-700">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-dark-900/50 text-gray-500 dark:text-gray-400 uppercase tracking-wider font-bold">
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700 w-16 text-center">{queriesCopy.columns?.srNo || 'SR No'}</th>
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700 w-36 text-center">{queriesCopy.columns?.date || 'Date'}</th>
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700 w-44">{queriesCopy.columns?.accountName || 'Account Name'}</th>
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700">{queriesCopy.columns?.description || 'Description'}</th>
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700 w-32">{queriesCopy.columns?.amount || 'Amount'}</th>
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700">{queriesCopy.columns?.bookscomplyComment || 'Bookscomply Comment'}</th>
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700">{queriesCopy.columns?.clientComment || 'Client Comment'}</th>
                        <th className="p-3 border-b border-gray-200 dark:border-dark-700 w-24 text-center">{queriesCopy.columns?.status || 'Status'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                      {queries.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="p-12 text-center text-gray-400 dark:text-gray-500">
                            <div className="flex flex-col items-center justify-center gap-2">
                              <FaClock size={24} className="opacity-40" />
                              <p className="font-medium text-sm">{queriesCopy.emptyTitle || 'No Pending Queries'}</p>
                              <p className="text-xs">{queriesCopy.emptyText || 'There are no pending bookkeeping queries listed at this time.'}</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        queries.map((q, index) => (
                          <React.Fragment key={index}>
                            <tr className="hover:bg-gray-50/50 dark:hover:bg-dark-800/30">
                              <td className="p-3 text-center font-bold text-gray-900 dark:text-white">
                                {q.srNo}
                              </td>
                              <td className="p-3 text-gray-700 dark:text-gray-300 font-medium text-center">
                                {q.date || "N/A"}
                              </td>
                              <td className="p-3 text-gray-700 dark:text-gray-300 font-medium">
                                {q.accountName || "N/A"}
                                {q.parentUserName && q.parentUserName !== userData?.name && (
                                  <span className="block text-xs text-primary-500 font-semibold mt-0.5 uppercase tracking-wider">
                                    Entity: {q.parentUserName}
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-gray-700 dark:text-gray-300">
                                {q.description || "N/A"}
                              </td>
                              <td className="p-3 text-gray-700 dark:text-gray-300 font-semibold">
                                {q.amount || "N/A"}
                              </td>
                              <td className="p-3 text-gray-700 dark:text-gray-300">
                                {q.bookscomplyComment || "N/A"}
                              </td>
                              <td className="p-3">
                                <textarea
                                  value={q.clientComment || ""}
                                  onChange={(e) => handleClientCommentChange(index, e.target.value)}
                                  disabled={isStep2Locked || q.isClientSubmitted}
                                  rows="1"
                                  className="w-full px-2.5 py-1.5 rounded-lg border border-primary-300 dark:border-dark-600 bg-white dark:bg-dark-900 text-gray-900 dark:text-white text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none resize-y disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-dark-900/50"
                                  placeholder={q.isClientSubmitted
                                    ? (queriesCopy.clientCommentSubmittedPlaceholder || 'First comment submitted')
                                    : (queriesCopy.clientCommentPlaceholder || 'Enter your comment here...')}
                                />
                              </td>
                              <td className="p-3 text-center">
                                {!q.isClientSubmitted ? (
                                  (q.clientComment && q.clientComment.trim()) ? (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold tracking-wide uppercase bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-400">
                                      {queriesCopy.statusComplete || 'Complete'}
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold tracking-wide uppercase bg-amber-100 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                                      {queriesCopy.statusActionRequired || 'Action Required'}
                                    </span>
                                  )
                                ) : !q.isAdminReplied ? (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-bold tracking-wide uppercase bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-400">
                                    {queriesCopy.statusComplete || 'Complete'}
                                  </span>
                                ) : !q.isClientReplySubmitted ? (
                                  (q.clientReply && q.clientReply.trim()) ? (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold tracking-wide uppercase bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-400">
                                      {queriesCopy.statusComplete || 'Complete'}
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold tracking-wide uppercase bg-amber-100 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                                      {queriesCopy.statusActionRequired || 'Action Required'}
                                    </span>
                                  )
                                ) : (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-bold tracking-wide uppercase bg-green-100 text-green-800 dark:bg-green-950/30 dark:text-green-400">
                                    {queriesCopy.statusComplete || 'Complete'}
                                  </span>
                                )}
                              </td>
                            </tr>

                            {/* Thread Conversation Sub-row */}
                            {(q.isClientSubmitted || q.isAdminReplied || (q.clientComment && q.clientComment.trim())) && (
                              <tr className="bg-gray-50/30 dark:bg-dark-900/10">
                                <td colSpan="8" className="p-4 border-b border-gray-200 dark:border-dark-700">
                                  <div className="pl-6 border-l-2 border-primary-500 space-y-3">
                                    <div className="bg-white dark:bg-dark-800 p-3 rounded-xl border border-gray-150 dark:border-dark-700 shadow-sm max-w-4xl">
                                      <div className="flex items-center mb-1">
                                        <span className="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider">Your Submitted Comment</span>
                                      </div>
                                      <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{q.clientComment}</p>
                                    </div>

                                    {q.isAdminReplied && (
                                      <div className="bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/10 dark:to-purple-950/10 p-3 rounded-xl border border-indigo-100 dark:border-indigo-900/50 shadow-sm max-w-4xl">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Bookscomply Team Reply</span>
                                          <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold">Staff Response</span>
                                        </div>
                                        <p className="text-xs text-gray-800 dark:text-gray-200 font-medium whitespace-pre-wrap">{q.bookscomplyReply}</p>
                                      </div>
                                    )}

                                    {q.isAdminReplied && (
                                      <>
                                        <div className="bg-white dark:bg-dark-800 p-3 rounded-xl border border-gray-150 dark:border-dark-700 shadow-sm max-w-4xl">
                                          <div className="flex items-center justify-between mb-1.5">
                                            <span className="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider">Your Follow-up Reply</span>
                                            {q.isClientReplySubmitted && (
                                              <span className="text-xs text-green-600 dark:text-green-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                                <FaCheck className="w-2.5 h-2.5" /> Submitted
                                              </span>
                                            )}
                                          </div>
                                          <textarea
                                            value={q.clientReply || ""}
                                            onChange={(e) => handleClientReplyChange(index, e.target.value)}
                                            disabled={isStep2Locked || q.isClientReplySubmitted}
                                            rows="2"
                                            className="w-full px-2.5 py-1.5 rounded-lg border border-primary-300 dark:border-dark-600 bg-white dark:bg-dark-900 text-gray-900 dark:text-white text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none resize-y disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-dark-900/50"
                                            placeholder={q.isClientReplySubmitted
                                              ? (queriesCopy.clientReplyLockedPlaceholder || 'Your reply is submitted and locked.')
                                              : (queriesCopy.clientReplyPlaceholder || "Provide your reply to Bookscomply's follow-up question here...")}
                                          />
                                        </div>

                                        {/* Bookscomply Team Follow-up Reply (Read-only) */}
                                        {q.isAdminSecondReplied && (
                                          <div className="bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/10 dark:to-purple-950/10 p-3 rounded-xl border border-indigo-100 dark:border-indigo-900/50 shadow-sm max-w-4xl">
                                            <div className="flex items-center justify-between mb-1">
                                              <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Bookscomply Team Follow-up Reply</span>
                                              <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold">Staff Response</span>
                                            </div>
                                            <p className="text-xs text-gray-800 dark:text-gray-200 font-medium whitespace-pre-wrap">{q.bookscomplySecondReply}</p>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50/50 dark:bg-dark-900/10">
                <div className="text-xs font-semibold flex items-center gap-2">
                  {autoSaveStatus === 'saving' && (
                    <span className="text-primary-600 dark:text-primary-400 flex items-center gap-1.5 animate-pulse">
                      <FaSpinner className="w-3 h-3 animate-spin" /> Saving changes automatically...
                    </span>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1.5">
                      <FaCheck className="w-3 h-3" /> {queriesCopy.autoSaveSaved || 'Draft auto-saved'}
                    </span>
                  )}
                  {autoSaveStatus === 'error' && (
                    <span className="text-red-500 dark:text-red-400 flex items-center gap-1.5">
                      <FaExclamationCircle className="w-3 h-3" /> {queriesCopy.autoSaveError || 'Auto-save failed'}
                    </span>
                  )}
                  {autoSaveStatus === 'idle' && (
                    <span className="text-gray-400 dark:text-gray-500 font-medium">
                      {queriesCopy.autoSaveNote || 'Note: Your comments are automatically saved as draft as you type.'}
                    </span>
                  )}
                </div>
                <div className="flex gap-3 w-full sm:w-auto justify-end">
                  <button
                    onClick={() => {
                      if (autoSaveTimerRef.current) {
                        clearTimeout(autoSaveTimerRef.current);
                      }
                      setShowQueriesModal(false);
                    }}
                    disabled={submittingQueries}
                    className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors disabled:opacity-50 text-sm font-semibold"
                  >
                    {queriesCopy.closeLabel || 'Close'}
                  </button>
                  <button
                    onClick={() => handleSaveClientComments(false, false, queries)}
                    disabled={submittingQueries || queries.length === 0 || isStep2Locked}
                    className="px-4 py-2.5 rounded-xl bg-primary-50 dark:bg-primary-950/20 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-950/40 border border-primary-200 dark:border-primary-800/30 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-semibold"
                  >
                    {submittingQueries ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaSave className="w-3.5 h-3.5" />}
                    <span>{queriesCopy.saveDraftLabel || 'Save Draft'}</span>
                  </button>
                  <button
                    onClick={() => handleSaveClientComments(true, false, queries)}
                    disabled={
                      submittingQueries ||
                      queries.length === 0 ||
                      isStep2Locked ||
                      queries.some(q => !q.isClientSubmitted && (!q.clientComment || !q.clientComment.trim())) ||
                      queries.some(q => q.isClientSubmitted && q.isAdminReplied && !q.isClientReplySubmitted && (!q.clientReply || !q.clientReply.trim())) ||
                      queries.every(q => q.isClientSubmitted && (!q.isAdminReplied || q.isClientReplySubmitted))
                    }
                    className="px-5 py-2.5 rounded-xl bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/25 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-bold"
                  >
                    {submittingQueries ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaCheck className="w-3.5 h-3.5" />}
                    <span>{queriesCopy.submitLabel || 'Submit Comments'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Step 3: Draft Financials Review Modal */}
      {showStep3Modal && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center animate-fade-in" onClick={() => setShowStep3Modal(false)}>
          <div className="bg-white dark:bg-dark-800 w-full h-full flex flex-col overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/20">
              <div>
                <h3 className="text-lg font-bold text-dark-800 dark:text-white flex items-center gap-2">
                  <FaFileExcel className="text-primary-500" /> {step3Copy.title || 'Step 3: Draft Financials Review'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {step3Copy.subtitle || 'Review the draft financial reports prepared by our bookkeeping team.'}
                </p>
              </div>
              <button onClick={() => { setShowStep3Modal(false); setStep3Action(""); setStep3Comment(""); }} className="p-2 rounded-xl hover:bg-gray-200/50 dark:hover:bg-dark-700 transition-colors">
                <FaTimes className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-6 overflow-y-auto flex-1 custom-scrollbar space-y-6">
              {/* Active Pending Draft section */}
              {(() => {
                const pendingEntry = [...(userData?.bookkeepingStep3History || [])]
                  .reverse()
                  .find(h => h.status === 'pending');

                if (!pendingEntry) {
                  return (
                    <div className="p-8 text-center bg-gray-50 dark:bg-dark-900/30 rounded-2xl border border-dashed border-gray-200 dark:border-dark-700 space-y-2">
                      <FaFile className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto" />
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">{step3Copy.emptyTitle || 'No Pending Draft Financials'}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                        {step3Copy.emptyText || 'Your draft financial reports are not ready yet. Our team will upload them and post a question for your review soon.'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    {/* Documents List */}
                    <div className="p-4 bg-primary-50/30 dark:bg-primary-950/5 rounded-2xl border border-primary-100 dark:border-primary-900 space-y-3 animate-fade-in">
                      <span className="block text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider">
                        {step3Copy.documentsLabel || 'Available Draft Documents'} ({pendingEntry.documents?.length || 0})
                      </span>
                      <div className="grid grid-cols-1 gap-2.5">
                        {pendingEntry.documents?.map((doc, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700 shadow-sm transition-all hover:shadow-md">
                            <div className="flex items-center gap-3 min-w-0 flex-1 mr-4">
                              <div className="p-2 bg-primary-50 dark:bg-primary-950/50 rounded-lg text-primary-600">
                                <FaFile className="w-4 h-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-bold text-gray-800 dark:text-gray-200 truncate" title={doc.originalName}>
                                  {doc.originalName}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {doc.size ? `${(doc.size / (1024 * 1024)).toFixed(2)} MB` : 'Size unknown'}
                                </p>
                              </div>
                            </div>
                            <a
                              href={`${process.env.REACT_APP_API_BASE_URL}${doc.fileUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1.5 bg-gray-50 dark:bg-dark-700 hover:bg-primary-50 dark:hover:bg-primary-950 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg text-xs font-bold transition-all border border-gray-200 dark:border-dark-600 hover:border-primary-200 dark:hover:border-primary-800/30 flex items-center gap-1.5 shrink-0"
                            >
                              <FaEye className="w-3.5 h-3.5" /> {step3Copy.viewDownloadLabel || 'View / Download'}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Question Box */}
                    <div className="p-5 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/10 dark:to-purple-950/10 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 space-y-5">

                      {pendingEntry.questions && pendingEntry.questions.length > 0 ? (
                        <div className="space-y-4">
                          <span className="block text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                            {step3Copy.questionsLabel || 'Please review the questions below'} ({pendingEntry.questions.length})
                          </span>
                          <div className="space-y-4">
                            {pendingEntry.questions.map((q, qIdx) => (
                              <div key={qIdx} className="bg-white/40 dark:bg-dark-900/35 p-4 rounded-xl border border-indigo-100/50 dark:border-indigo-900/30 space-y-1.5 backdrop-blur-sm">
                                <h4 className="text-xs font-bold text-gray-950 dark:text-white leading-relaxed">
                                  <span className="text-indigo-600 dark:text-indigo-400 font-extrabold mr-1">Q{qIdx + 1}:</span>
                                  {q.questionText}
                                </h4>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <span className="block text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                            {step3Copy.reviewRequestLabel || 'Review Request'}
                          </span>
                          <h4 className="text-sm font-bold text-gray-950 dark:text-white leading-relaxed">
                            "{pendingEntry.question || step3Copy.defaultQuestion || 'Please review the draft financials above.'}"
                          </h4>
                        </div>
                      )}

                      {/* Interactive Submission Decision Buttons */}
                      <div className="space-y-3 pt-2 border-t border-indigo-100/40 dark:border-indigo-900/30">
                        <span className="block text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                          {step3Copy.decisionLabel || 'Overall Review Decision'}
                        </span>
                        <div className="flex gap-4">
                          <button
                            onClick={() => { setStep3Action("yes"); setStep3Comment(""); }}
                            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 border-2 ${step3Action === 'yes'
                                ? 'bg-green-500 text-white border-green-500 shadow-lg shadow-green-500/20'
                                : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-dark-700 hover:border-green-300 dark:hover:border-green-800/40 hover:bg-green-50/20 dark:hover:bg-green-950/10'
                              }`}
                          >
                            <FaCheckCircle className={`w-4 h-4 ${step3Action === 'yes' ? 'text-white' : 'text-green-500'}`} />
                            <span>{step3Copy.approveLabel || 'Yes, I approve everything'}</span>
                          </button>
                          <button
                            onClick={() => { setStep3Action("no"); }}
                            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 border-2 ${step3Action === 'no'
                                ? 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-500/20'
                                : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-dark-700 hover:border-red-300 dark:hover:border-red-800/40 hover:bg-red-50/20 dark:hover:bg-red-950/10'
                              }`}
                          >
                            <FaExclamationCircle className={`w-4 h-4 ${step3Action === 'no' ? 'text-white' : 'text-red-550'}`} />
                            <span>{step3Copy.rejectLabel || 'No, I have changes'}</span>
                          </button>
                        </div>
                      </div>

                      {/* Comment Input */}
                      {step3Action === 'no' && (
                        <div className="space-y-2 animate-fade-in pt-2">
                          <label className="block text-xs font-bold text-red-500 uppercase tracking-wider">
                            {step3Copy.commentLabel || 'What adjustments are needed? (Required)'}
                          </label>
                          <textarea
                            rows={3}
                            value={step3Comment}
                            onChange={(e) => setStep3Comment(e.target.value)}
                            placeholder={step3Copy.commentPlaceholder || 'Please specify any credit card balances, missing receipts, adjustments, or comments here...'}
                            className="w-full p-3 text-xs bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-xl text-gray-950 dark:text-white outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all shadow-inner"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* History Section */}
              {userData?.bookkeepingStep3History?.length > 0 && (
                <div className="space-y-3.5 pt-4 border-t border-gray-100 dark:border-dark-700">
                  <span className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {step3Copy.historyTitle || 'Draft Iteration History'}
                  </span>
                  <div className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar pr-1">
                    {[...(userData.bookkeepingStep3History)].reverse().map((hist, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 dark:bg-dark-900/25 border border-gray-100 dark:border-dark-700 rounded-2xl text-xs space-y-3 transition-colors hover:bg-gray-100/50 dark:hover:bg-dark-900/40">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-400 dark:text-gray-500">
                            {new Date(hist.uploadedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${hist.status === 'approved'
                              ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400'
                              : hist.status === 'rejected'
                                ? 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400'
                                : 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400'
                            }`}>
                            {hist.status}
                          </span>
                        </div>

                        {hist.questions && hist.questions.length > 0 ? (
                          <div className="space-y-2">
                            <span className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                              Questions & Answers
                            </span>
                            <div className="space-y-2 pl-2 border-l-2 border-indigo-200 dark:border-indigo-800">
                              {hist.questions.map((q, qIdx) => (
                                <div key={qIdx} className="text-xs">
                                  <p className="text-gray-800 dark:text-gray-200 font-bold">
                                    Q{qIdx + 1}: {q.questionText}
                                  </p>
                                  <div className="flex flex-wrap gap-1.5 mt-1">
                                    {q.options?.map((opt, optIdx) => (
                                      <span
                                        key={optIdx}
                                        className={`px-2 py-0.5 rounded text-xs font-semibold border transition-all ${q.selectedAnswer === opt
                                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border-indigo-300 dark:border-indigo-800/50'
                                            : 'bg-white text-gray-400 dark:bg-dark-800 dark:text-gray-500 border-gray-200 dark:border-dark-700'
                                          }`}
                                      >
                                        {opt}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <span className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                              Question asked
                            </span>
                            <p className="text-gray-800 dark:text-gray-200 font-bold italic leading-relaxed">
                              "{hist.question || step3Copy.defaultQuestion || 'Review the financials.'}"
                            </p>
                          </div>
                        )}

                        <div className="space-y-1.5">
                          <span className="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                            Documents uploaded
                          </span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {hist.documents?.map((doc, docIdx) => (
                              <a
                                key={docIdx}
                                href={`${process.env.REACT_APP_API_BASE_URL}${doc.fileUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between p-2 bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                              >
                                <span className="truncate mr-2 flex-1 font-medium">{doc.originalName}</span>
                                <FaEye className="shrink-0 w-3 h-3 text-gray-400" />
                              </a>
                            ))}
                          </div>
                        </div>

                        {hist.status === 'rejected' && hist.clientComment && (
                          <div className="p-3 bg-red-50/50 dark:bg-red-950/10 border-l-2 border-red-500 rounded-xl text-red-700 dark:text-red-400 animate-fade-in font-medium">
                            <span className="font-bold text-xs uppercase block tracking-wider mb-0.5">Your Feedback:</span>
                            <span className="italic font-medium">"{hist.clientComment}"</span>
                          </div>
                        )}

                        {hist.clientActionAt && (
                          <div className="text-xs text-gray-400 text-right">
                            Responded on {new Date(hist.clientActionAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-5 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3 bg-gray-50/50 dark:bg-dark-900/20">
              <button
                onClick={() => { setShowStep3Modal(false); setStep3Action(""); setStep3Comment(""); }}
                disabled={step3Submitting}
                className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-600 dark:text-gray-400 transition-colors disabled:opacity-50 text-sm font-semibold"
              >
                Close
              </button>
              {step3Action && (
                <button
                  onClick={() => handleSubmitStep3Response(step3Action === 'yes' ? 'approve' : 'reject')}
                  disabled={step3Submitting}
                  className={`px-5 py-2.5 rounded-xl text-white shadow-lg flex items-center gap-2 text-sm font-bold transition-all ${step3Action === 'yes'
                      ? 'bg-green-500 hover:bg-green-600 shadow-green-500/25'
                      : 'bg-red-500 hover:bg-red-600 shadow-red-500/25'
                    }`}
                >
                  {step3Submitting ? (
                    <FaSpinner className="w-4.5 h-4.5 animate-spin" />
                  ) : step3Action === 'yes' ? (
                    <FaCheck className="w-4 h-4" />
                  ) : (
                    <FaSave className="w-4 h-4" />
                  )}
                  <span>{step3Action === 'yes'
                    ? (step3Copy.approveSubmitLabel || 'Approve & Submit')
                    : (step3Copy.submitFeedbackLabel || 'Submit Feedback')}</span>
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}

    </div>
  );
};

export default UserBookkeeping;